import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  LightningIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { KeyValue } from '@/components/ui/key-value';

import {
  createOrderDraft,
  dismissCopyNotification,
  executeCopyNotification,
  getCopyNotification,
  type CopyNotification,
} from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

export default function CopyConfirm() {
  const { notificationId = '' } = useParams();
  const t = useT();
  const auth = useAuth();
  const navigate = useNavigate();
  useTelegramBackButton();
  useNavStackEntry({ label: t('copy.confirm.breadcrumb') });

  const [notif, setNotif] = useState<CopyNotification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const ready = auth.state === 'ready';

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCopyNotification(notificationId);
      setNotif(data);
      setAmount(String(data.suggestedUsd));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [notificationId]);

  useEffect(() => {
    if (!ready) return;
    void reload();
  }, [ready, reload]);

  const onConfirm = useCallback(async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      const amtN = amount.trim() === '' ? undefined : Number(amount);
      const intent = await executeCopyNotification(
        notificationId,
        amtN !== undefined && Number.isFinite(amtN) ? amtN : undefined,
      );
      // Mint the order draft up front so SignOrder receives an orderId; the
      // intent params are kept in the query string as a silent-recreate
      // fallback if the draft expires (same contract as the market buy flow).
      const draft = await createOrderDraft({
        marketSlug: intent.slug,
        outcomeLabel: intent.outcome,
        side: intent.side,
        amount: intent.amount,
      });
      const qs = new URLSearchParams({
        orderId: draft.orderId,
        slug: intent.slug,
        outcome: intent.outcome,
        side: intent.side,
        amount: String(intent.amount),
      });
      navigate(`/sign-order?${qs.toString()}`);
    } catch (err) {
      setActionError((err as Error).message);
      setSubmitting(false);
    }
  }, [amount, navigate, notificationId]);

  const onDismiss = useCallback(async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      await dismissCopyNotification(notificationId);
      navigate('/copy');
    } catch (err) {
      setActionError((err as Error).message);
      setSubmitting(false);
    }
  }, [navigate, notificationId]);

  const eyebrow = useMemo(
    () => (
      <span className="inline-flex items-center gap-1.5">
        <LightningIcon weight="fill" className="size-3" />
        {t('copy.confirm.eyebrow')}
      </span>
    ),
    [t],
  );

  if (auth.state === 'loading' || (loading && notif === null)) {
    return (
      <AuthLoadingScreen
        title={t('copy.confirm.loading_title')}
        description={t('copy.confirm.loading_desc')}
        shortDescription={t('copy.confirm.loading_short')}
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: t('copy.confirm.eyebrow'), color: 'bg-primary', delay: '0.4s' },
        ]}
      />
    );
  }

  if (error || notif === null) {
    return (
      <ScreenShell eyebrow={eyebrow} title={t('copy.confirm.title')}>
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('copy.confirm.load_failed')}</AlertTitle>
          <AlertDescription>{error ?? t('copy.confirm.not_found')}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const alreadyHandled =
    notif.status === 'EXECUTED' || notif.status === 'DISMISSED';

  return (
    <ScreenShell
      eyebrow={eyebrow}
      title={t('copy.confirm.title')}
      description={t('copy.confirm.description', { leader: notif.leaderLabel })}
    >
      <NavBar />

      <Card>
        <CardHeader>
          <CardEyebrow>
            {notif.side === 'BUY' ? (
              <ArrowUpIcon weight="fill" className="size-3 text-emerald-500" />
            ) : (
              <ArrowDownIcon weight="fill" className="size-3 text-rose-500" />
            )}
            {notif.side === 'BUY' ? t('copy.confirm.side_buy') : t('copy.confirm.side_sell')}
          </CardEyebrow>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-foreground text-sm font-medium leading-snug">
            {notif.marketQuestion}
          </p>
          <KeyValue label={t('copy.confirm.field_outcome')} value={notif.outcome} />
          <KeyValue
            label={t('copy.confirm.field_price')}
            value={`${(notif.price * 100).toFixed(0)}¢`}
          />
          <KeyValue
            label={t('copy.confirm.field_leader')}
            value={notif.leaderLabel}
          />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-[13px]">
              {t('copy.confirm.field_status')}
            </span>
            <Badge
              tone={
                notif.status === 'EXECUTED'
                  ? 'success'
                  : notif.status === 'DISMISSED'
                    ? 'neutral'
                    : 'primary'
              }
            >
              {notif.status}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardEyebrow>
            <LightningIcon weight="fill" className="size-3" />
            {t('copy.confirm.amount_title')}
          </CardEyebrow>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
              {t('copy.confirm.amount_label')}
            </span>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              min="1"
              step="1"
              inputMode="decimal"
              disabled={alreadyHandled}
            />
          </label>
          {actionError && (
            <Alert tone="danger">
              <WarningIcon weight="fill" className="size-4" />
              <AlertTitle>{t('copy.confirm.action_error')}</AlertTitle>
              <AlertDescription>{actionError}</AlertDescription>
            </Alert>
          )}
          {alreadyHandled ? (
            <Alert tone="info">
              <AlertTitle>{t('copy.confirm.handled_title')}</AlertTitle>
              <AlertDescription>{t('copy.confirm.handled_desc')}</AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-2">
              <Button type="button" onClick={onConfirm} disabled={submitting}>
                <LightningIcon weight="bold" className="size-4" />
                {submitting ? t('copy.confirm.confirming') : t('copy.confirm.confirm_btn')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onDismiss}
                disabled={submitting}
              >
                <XCircleIcon weight="bold" className="size-4" />
                {t('copy.confirm.dismiss_btn')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </ScreenShell>
  );
}
